/**
 * DS-internal hook : une rangée qui défile en largeur, parcourue à la souris, au web. Le `<ScrollView horizontal>`
 * de react-native-web défile au doigt et au pavé tactile, mais sa barre est masquée : à la souris, rien ne le
 * faisait bouger. Ici, quand la rangée déborde :
 *
 *  - la molette verticale la fait défiler en largeur, du même cran ; arrivée à un bout, elle rend la main à la
 *    page, qui défile comme avant. Un défilement de la page déjà lancé qui passe sous le pointeur la laisse en
 *    place (le navigateur ne laisse annuler que le premier cran d'un geste). Le zoom (Ctrl + molette) et un geste
 *    déjà horizontal (pavé tactile, Maj + molette) restent au navigateur ;
 *  - le cliquer-glisser la fait suivre la souris ; au-delà de `DRAG_THRESHOLD_PX`, l'appui est un glisser, et le
 *    clic qui le termine n'atteint pas le choix sous le pointeur. En deçà, c'est un clic comme avant.
 *
 * Le doigt et le stylet gardent le défilement du navigateur. Au natif, rien : pas de souris.
 */
import React, { useEffect } from "react"
import { Platform } from "react-native"

import { isEditableTarget } from "../utils/shutterKey"
import { wheelPixels } from "./useWheelRelay"

/** Au-delà de ce déplacement, en pixels, un appui devient un glisser. */
export const DRAG_THRESHOLD_PX = 5

export function useMouseScroll(scroller: React.RefObject<unknown>): void {
    useEffect(() => {
        // `Platform?.` : sous Jest, un test qui simule `react-native` sans `Platform` rend une `<HorizontalList>`.
        if (Platform?.OS !== "web") return undefined
        const node = (scroller.current as { getScrollableNode?: () => unknown } | null)?.getScrollableNode?.() as
            HTMLElement | undefined
        const view = node?.ownerDocument?.defaultView
        if (node == null || view == null) return undefined

        const room = () => node.scrollWidth - node.clientWidth

        const onWheel = (event: WheelEvent) => {
            if (!event.cancelable || event.ctrlKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return
            const left = wheelPixels(event, node.clientWidth)
            // Une rangée qui tient n'a pas de place : elle est « au bout » dans les deux sens.
            const atEnd = left < 0 ? node.scrollLeft <= 0 : node.scrollLeft >= room() - 1
            if (atEnd) return
            event.preventDefault()
            node.scrollLeft += left
        }

        let press: { x: number; scrollLeft: number; dragging: boolean } | null = null

        const swallowClick = (event: MouseEvent) => {
            event.stopPropagation()
            event.preventDefault()
        }
        const onPointerMove = (event: PointerEvent) => {
            if (press == null) return
            // Le relâché perdu (la fenêtre a perdu le focus, un menu contextuel) : plus de bouton, plus de glisser.
            if (event.buttons === 0) {
                release()
                return
            }
            const dx = event.clientX - press.x
            if (!press.dragging && Math.abs(dx) < DRAG_THRESHOLD_PX) return
            press.dragging = true
            node.scrollLeft = press.scrollLeft - dx
        }
        const release = () => {
            view.removeEventListener("pointermove", onPointerMove)
            view.removeEventListener("pointerup", onPointerUp)
            view.removeEventListener("pointercancel", onPointerUp)
            press = null
        }
        // Le clic suit le relâché dans la même tâche, sur l'ancêtre commun de l'appui et du relâché : relâché hors de
        // la rangée, il irait à ce qui la contient (la carte pressable d'`<ImageAttachment>`). Il est donc pris à la
        // fenêtre, et l'écouteur part au tour suivant plutôt que d'avaler le prochain vrai clic.
        const onPointerUp = () => {
            if (press?.dragging) {
                view.addEventListener("click", swallowClick, { capture: true, once: true })
                view.setTimeout(() => view.removeEventListener("click", swallowClick, { capture: true }), 0)
            }
            release()
        }
        const onPointerDown = (event: PointerEvent) => {
            if (event.pointerType !== "mouse" || event.button !== 0 || room() <= 0 || isEditableTarget(event.target))
                return
            press = { x: event.clientX, scrollLeft: node.scrollLeft, dragging: false }
            view.addEventListener("pointermove", onPointerMove)
            view.addEventListener("pointerup", onPointerUp)
            view.addEventListener("pointercancel", onPointerUp)
        }
        // Pendant l'appui, ni sélection de texte ni glisser natif d'une image : ils prendraient le geste.
        const holdGesture = (event: Event) => {
            if (press != null) event.preventDefault()
        }

        node.addEventListener("wheel", onWheel, { passive: false })
        node.addEventListener("pointerdown", onPointerDown)
        node.addEventListener("selectstart", holdGesture)
        node.addEventListener("dragstart", holdGesture)
        return () => {
            release()
            node.removeEventListener("wheel", onWheel)
            node.removeEventListener("pointerdown", onPointerDown)
            node.removeEventListener("selectstart", holdGesture)
            node.removeEventListener("dragstart", holdGesture)
            view.removeEventListener("click", swallowClick, { capture: true })
        }
    }, [scroller])
}
